import type {
  Answers,
  BuiltSchema,
  Classifier,
  Question,
  Section,
} from '@zg/shared/property';
import { visibleQuestions } from '@zg/shared/property';

export interface PropertyReviewSummaryProps {
  schema: BuiltSchema;
  classifier: Classifier;
  answers: Answers;
  onEditSection: (sectionId: string) => void;
}

/**
 * Dense mono-style read view of every answered question, grouped by section.
 * Rendered inside the `review` section of the adaptive form (PropertyForm.tsx).
 */
export function PropertyReviewSummary({
  schema,
  classifier,
  answers,
  onEditSection,
}: PropertyReviewSummaryProps) {
  const visible = visibleQuestions(schema, classifier, answers);
  const dataSections = schema.sections.filter((s) => s.id !== 'review');

  return (
    <div className="flex flex-col gap-8">
      <ClassifierBlock classifier={classifier} />

      {dataSections.map((section) => (
        <SectionBlock
          key={section.id}
          section={section}
          questions={visible.filter((q) => q.section === section.id)}
          answers={answers}
          onEdit={() => onEditSection(section.id)}
        />
      ))}
    </div>
  );
}

function ClassifierBlock({ classifier }: { classifier: Classifier }) {
  const rows: Array<[string, string]> = [
    ['PROPERTY_TYPE', classifier.propertyType.toUpperCase()],
    ['OWNERSHIP', classifier.ownership.replace(/-/g, '_').toUpperCase()],
    ['STATE', classifier.state],
    ['COVERAGES', classifier.coverages.join(', ').toUpperCase() || '—'],
    ['FEATURES', classifier.features.join(', ').toUpperCase() || 'NONE'],
    ['EMPLOYEES', classifier.employees.toUpperCase()],
    ['TIV_BUCKET', classifier.tivBucket],
  ];
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4">
        <span className="font-mono text-[0.6875rem] uppercase tracking-[0.125em] text-ink-subtle">
          &gt; CLASSIFICATION
        </span>
      </div>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 font-mono text-[0.8125rem] border-t border-hairline pt-3">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-4">
            <dt className="text-ink-subtle tracking-[0.08em]">&gt; {k}</dt>
            <dd className="text-ink text-right tabular-nums truncate">
              {v}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function SectionBlock({
  section,
  questions,
  answers,
  onEdit,
}: {
  section: Section;
  questions: Question[];
  answers: Answers;
  onEdit: () => void;
}) {
  const answered = questions.filter((q) => hasAnswer(answers[q.id]));
  const isEmpty = answered.length === 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4">
        <span className="font-mono text-[0.6875rem] uppercase tracking-[0.125em] text-ink-subtle">
          &gt; {section.eyebrow}
        </span>
        <button
          type="button"
          onClick={onEdit}
          className="font-mono text-[0.75rem] text-blue-deep hover:text-blue transition-colors uppercase tracking-[0.08em]"
        >
          [ edit ]
        </button>
      </div>
      {isEmpty ? (
        <p className="font-mono text-[0.8125rem] text-ink-subtle border-t border-hairline pt-3">
          &gt; NO_ANSWERS_PROVIDED
        </p>
      ) : (
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 font-mono text-[0.8125rem] border-t border-hairline pt-3">
          {answered.map((q) => (
            <div
              key={q.id}
              className="flex items-start justify-between gap-4"
            >
              <dt className="text-ink-subtle tracking-[0.02em] leading-snug">
                {q.label}
              </dt>
              <dd className="text-ink text-right leading-snug max-w-[65%]">
                {formatAnswer(q, answers[q.id])}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

function hasAnswer(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function formatAnswer(q: Question, value: unknown): string {
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    const labels = value.map((v) => labelFor(q, String(v)));
    return labels.join(', ');
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    if (q.type === 'currency') {
      return `$${new Intl.NumberFormat('en-US').format(value)}`;
    }
    if (q.type === 'percent') {
      return `${value}%`;
    }
    return String(value);
  }
  if (typeof value === 'string') {
    return labelFor(q, value);
  }
  return String(value);
}

function labelFor(q: Question, value: string): string {
  if (q.options) {
    const match = q.options.find((o) => o.value === value);
    if (match) return match.label;
  }
  return value;
}
